import { useState, useEffect, useCallback } from 'react';
import type { Flight, FlightsFilter } from '../types/flight';
import {
    createFlight,
    getFlights,
    getAllTripTags,
    updateFlight as apiUpdateFlight,
    deleteFlight as apiDeleteFlight,
} from '../services/api/flightsApi';
import { INITIAL_EMPTY_FLIGHT, iataOrFallback } from '../components/flights/flightFormUtils';

const FLIGHTS_PER_PAGE = 10;

export const useFlightService = (isAuthenticated?: boolean, language?: string) => {
    const lang = language || (typeof navigator !== 'undefined' ? navigator.language : 'en');
    const [flights, setFlights] = useState<Flight[]>([]);
    const [flightsTotal, setFlightsTotal] = useState(0);
    const [flightsTotalDistance, setFlightsTotalDistance] = useState(0);
    const [flightsTotalPrice, setFlightsTotalPrice] = useState(0);
    const [flightsTotalVisitedCities, setFlightsTotalVisitedCities] = useState(0);
    const [flightsPage, setFlightsPage] = useState(1);
    const [flightsFilter, setFlightsFilter] = useState<FlightsFilter | null>(null);
    const [flightTags, setFlightTags] = useState<string[]>([]);
    const [showFlightsFilter, setShowFlightsFilter] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showAddFlight, _setShowAddFlight] = useState(false);
    const [flightToEdit, setFlightToEdit] = useState<Flight | null>(null);
    const [selectedFlightId, setSelectedFlightId] = useState<number | null>(null);
    const [createError, setCreateError] = useState<string | null>(null);
    const [isSavingFlight, setIsSavingFlight] = useState(false);
    const setShowAddFlight = (show: boolean) => {
        _setShowAddFlight(show);
        if (!show) {
            setFlightToEdit(null);
            setCreateError(null);
        }
    };
    const [newFlight, setNewFlight] = useState<Partial<Flight>>({ ...INITIAL_EMPTY_FLIGHT });

    const openNewFlightForm = () => {
        setFlightToEdit(null);
        setNewFlight({ ...INITIAL_EMPTY_FLIGHT });
        setCreateError(null);
        _setShowAddFlight(true);
    };

    useEffect(() => {
        if (isAuthenticated === false) {
            setFlights([]);
            setLoading(false);
            return;
        }
        if (isAuthenticated !== true) {
            setLoading(true);
            return;
        }
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const data = await getFlights(lang, flightsPage, FLIGHTS_PER_PAGE, flightsFilter ?? undefined);
                if (!cancelled) {
                    setFlightsTotal(Number(data?.total) || 0);
                    setFlightsTotalDistance(Number(data?.total_distance) || 0);
                    setFlightsTotalPrice(Number(data?.total_price) || 0);
                    setFlightsTotalVisitedCities(Number(data?.total_visited_cities) ?? 0);
                    const items = (data?.items ?? []).map((f: any) => ({
                        ...f,
                        class: f.flightClass,
                        isInternational: f.is_international,
                        departure_airport_info: f.departure_airport_info ?? null,
                        arrival_airport_info: f.arrival_airport_info ?? null,
                    }));
                    setFlights(items);
                }
            } catch {
                if (!cancelled) setFlights([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isAuthenticated, lang, flightsPage, flightsFilter]);

    const refetchFlights = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const data = await getFlights(lang, flightsPage, FLIGHTS_PER_PAGE, flightsFilter ?? undefined);
            setFlightsTotal(Number(data?.total) ?? 0);
            setFlightsTotalDistance(Number(data?.total_distance) ?? 0);
            setFlightsTotalPrice(Number(data?.total_price) ?? 0);
            setFlightsTotalVisitedCities(Number(data?.total_visited_cities) ?? 0);
            const items = (data?.items ?? []).map((f: any) => ({
                ...f,
                class: f.flightClass,
                isInternational: f.is_international,
                departure_airport_info: f.departure_airport_info ?? null,
                arrival_airport_info: f.arrival_airport_info ?? null,
            }));
            setFlights(items);
        } catch {
            setFlights([]);
        }
    }, [isAuthenticated, lang, flightsPage, flightsFilter]);

    const loadFlightTags = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            setFlightTags(await getAllTripTags());
        } catch {
            setFlightTags([]);
        }
    }, [isAuthenticated]);

    const addFlight = async () => {
        const n = newFlight;
        if (!n.tag?.trim() || !n.date || !n.flightNumber?.trim() || !n.departureCity?.trim() || !n.arrivalCity?.trim()) return;
        if (isSavingFlight) return;
        setCreateError(null);
        setIsSavingFlight(true);
        const payload = {
            date: n.date,
            tag: n.tag,
            departure: iataOrFallback(n.departure, n.departureCity),
            departureCity: n.departureCity,
            arrival: iataOrFallback(n.arrival, n.arrivalCity),
            arrivalCity: n.arrivalCity,
            airline: n.airline || '-',
            airline_iata: n.airline_iata,
            flightNumber: n.flightNumber,
            duration: n.duration || '0:00',
            distance: Number(n.distance) || 0,
            aircraft: n.aircraft || '',
            aircraft_reg: n.aircraft_reg,
            departure_time: n.departure_time,
            arrival_time: n.arrival_time,
            flightClass: n.class || 'Economy',
            price: Number(n.price) || 0,
            departureSubdivision: n.departureSubdivision || undefined,
            arrivalSubdivision: n.arrivalSubdivision || undefined,
        };
        try {
            const created = await createFlight(payload as any, lang);
            if (created) {
                const c = created as any;
                const mapped = {
                    ...created,
                    class: c.flightClass,
                    isInternational: c.is_international,
                    departure_airport_info: c.departure_airport_info ?? null,
                    arrival_airport_info: c.arrival_airport_info ?? null,
                } as Flight;
                setFlights(prev => [mapped, ...prev.filter(f => f.id !== mapped.id)]);
                setFlightsTotal(prev => prev + 1);
                setFlightsTotalDistance(prev => prev + (Number(mapped.distance) || 0));
                setFlightsTotalPrice(prev => prev + (Number(mapped.price) || 0));
                setNewFlight({ ...INITIAL_EMPTY_FLIGHT });
                setShowAddFlight(false);
                void refetchFlights();
            }
        } catch (e) {
            console.error('Create flight failed:', e);
            setCreateError(e instanceof Error ? e.message : String(e));
        } finally {
            setIsSavingFlight(false);
        }
    };

    const updateFlight = async (id: number, data: Partial<Flight>) => {
        try {
            const updated = await apiUpdateFlight(id, data, lang);
            const u = updated as any;
            setFlights(prev => prev.map(f => f.id === id ? {
                ...updated,
                class: u.flightClass,
                isInternational: u.is_international,
                departure_airport_info: u.departure_airport_info ?? null,
                arrival_airport_info: u.arrival_airport_info ?? null,
            } as Flight : f));
        } catch (e) {
            console.error('Update flight failed:', e);
        }
    };

    const deleteFlight = async (flight: Flight) => {
        try {
            await apiDeleteFlight(flight.id);
            setFlights(prev => prev.filter(f => f.id !== flight.id));
            setFlightsTotal(prev => Math.max(0, prev - 1));
            setFlightsTotalDistance(prev => Math.max(0, prev - (flight.distance ?? 0)));
            setFlightsTotalPrice(prev => Math.max(0, prev - (flight.price ?? 0)));
            if (flightToEdit?.id === flight.id) {
                setFlightToEdit(null);
                setShowAddFlight(false);
            }
        } catch (e) {
            console.error('Delete flight failed:', e);
        }
    };

    const editFlight = (flight: Flight) => {
        setFlightToEdit(flight);
        setNewFlight({
            date: flight.date,
            tag: flight.tag,
            departure: flight.departure,
            departureCity: flight.departureCity,
            arrival: flight.arrival,
            arrivalCity: flight.arrivalCity,
            airline: flight.airline,
            airline_iata: flight.airline_iata,
            flightNumber: flight.flightNumber,
            duration: flight.duration,
            distance: flight.distance,
            aircraft: flight.aircraft ?? '',
            aircraft_reg: flight.aircraft_reg,
            departure_time: flight.departure_time ?? '',
            arrival_time: flight.arrival_time ?? '',
            class: flight.class ?? (flight as any).flightClass ?? 'Economy',
            price: flight.price ?? 0,
        });
        _setShowAddFlight(true);
    };

    return {
        flights,
        setFlights,
        loading,
        showAddFlight,
        setShowAddFlight,
        openNewFlightForm,
        newFlight,
        setNewFlight,
        addFlight,
        flightToEdit,
        editFlight,
        updateFlight,
        deleteFlight,
        createError,
        isSavingFlight,
        selectedFlightId,
        setSelectedFlightId,
        flightsTotal,
        flightsTotalDistance,
        flightsTotalPrice,
        flightsTotalVisitedCities,
        flightsPage,
        setFlightsPage,
        flightsPerPage: FLIGHTS_PER_PAGE,
        flightsFilter,
        setFlightsFilter,
        flightTags,
        loadFlightTags,
        showFlightsFilter,
        setShowFlightsFilter,
        refetchFlights,
    };
};
